import { faAngleRight, faCheck, faPlus, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Group, Text } from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { IconUpload } from '@tabler/icons-react';
import _ from 'lodash';
import React, { useContext, useEffect, useRef, useState } from 'react';
import './EnrichmentProductVariant.css';
import { BASE_URL } from '../../api/EndPoints';
import { createB2BAPI } from '../../api/Interceptor';
import B2BPillsInput from '../../components/common/B2BPillsInput';
import B2BSelect from '../../components/common/B2BSelect';
import { MappedProductContext } from '../../Context/MappedProductContext';


const MappedProductVariant = ({ vendorId }) => {
    const { product, setProduct, handleChange, inputError, setInputError } = useContext(MappedProductContext);
    const [attributes, setAttributes] = useState({});
    const [selectedPairs, setSelectedPairs] = useState([{ key: '', values: [] }]);
    const [combinations, setCombinations] = useState([]);
    const [variants, setVariants] = useState([]);
    const [activeTab, setActiveTab] = useState('Transaction');
    const [expandedRows, setExpandedRows] = useState([]);
    const [productVariantMap, setProductVariantMap] = useState(new Map())
    const isRowExpanded = (index) => expandedRows.includes(index);
    const openRef = useRef(null);
    const B2B_API = createB2BAPI();
    const [existingPairs, setExistingPairs] = useState([]);
    const [existingPairsKeys, setExistingPairsKeys] = useState([]);

    useEffect(() => {
        const keyValuePairs = product.productVariants.map((productVariant) => {
            const filteredVariants = productVariant.variants
                .filter(variant => variant.name !== 'Solid / Pattern');
            return filteredVariants.map(variant => ({
                [variant.name]: variant.value
            }));
        })
            .reduce((acc, objArray) => {
                objArray.forEach(obj => {
                    const [key, value] = Object.entries(obj)[0];
                    if (acc[key]) {
                        acc[key] = [...new Set([...acc[key], value])];

                    } else {
                        acc[key] = [value];
                    }
                });
                return acc;
            }, {});

        setExistingPairs(keyValuePairs);
        const existingKeys = product.productVariants.map(productVariant => productVariant.variants.map(variant => variant.name)).flat();
        const uniqueKeys = [...new Set(existingKeys)];
        setExistingPairsKeys(uniqueKeys);
    }, [product.id]);

    useEffect(() => {
        fetchVariant();
    }, []);

    useEffect(() => {
        generateProductVariantMap()
    }, [product?.productVariants])

    useEffect(() => {
        updateCombinations(selectedPairs);
    }, [variants, selectedPairs]);

    const generateCombinations = (lists, variants = []) => {
        const result = [];
        const generateCombinationsRecursive = (depth, current) => {
            if (depth === lists.length) {
                result.push([...current]);
                return;
            }
            for (const element of lists[depth]) {
                current.push(element);
                generateCombinationsRecursive(depth + 1, current);
                current.pop();
            }
        };
        generateCombinationsRecursive(0, []);
        return result;
    };

    const generateProductVariantMap = () => {
        if (product.productVariants && _.size(product.productVariants) > 0 && _.size(product.productVariants[0].variants) > 0) {
            const productMap = new Map();
            _.map(product.productVariants, pv => {
                const variantIds = _.map(pv.variants, va => va.id);
                // Sort the variant IDs to ensure a consistent key
                const sortedVariantIds = _.sortBy(variantIds);
                productMap.set(JSON.stringify(sortedVariantIds), pv); // Use sorted keys
            });
            setProductVariantMap(productMap);
        }
    };

    const findMatchingVariant = (combination) => {
        for (let i = 0; i < combination.length; i++) {
            const partialCombination = _.slice(combination, 0, i + 1);
            const flattenedCombination = _.flatten(partialCombination);
            const sortedFlattenedCombination = _.sortBy(flattenedCombination);
            const key = JSON.stringify(sortedFlattenedCombination);
            if (productVariantMap.has(key)) {
                const variant = productVariantMap.get(key);
                productVariantMap.delete(key);
                return variant;
            }
        }
        return null;
    };

    const updateCombinations = async (pairs) => {
        const hasAnyKey = pairs && pairs.some(pair => Boolean(pair.key));
        const hasSelectedPairs = pairs && pairs.some(pair => pair.key && pair.values && pair.values.length > 0);
        if (!hasSelectedPairs) {
            if (hasAnyKey) {
                setCombinations([]);
                setProduct(prev => ({
                    ...prev,
                    newProductVariants: []
                }));
                return;
            }
            const existingVariants = (Array.isArray(product?.newProductVariants) && product.newProductVariants.length > 0)
                ? product.newProductVariants
                : (Array.isArray(product?.productVariants) && product.productVariants.length > 0)
                    ? product.productVariants
                    : [];
            setCombinations(existingVariants);
            if (existingVariants.length > 0) {
                setProduct(prev => ({
                    ...prev,
                    newProductVariants: (Array.isArray(prev.newProductVariants) && prev.newProductVariants.length > 0)
                        ? prev.newProductVariants
                        : existingVariants,
                }));
            }
            return;
        }

        const newCombinations = generateCombinations(pairs.map(pair => pair.values), variants);
        const prodVariants = [];
        setCombinations(newCombinations);
        _.forEach(newCombinations, combination => {
            let variant = findMatchingVariant(combination);
            if (variant === null) {
                variant = {}
            }
            variant['variants'] = _.map(combination, c => _.find(variants, v => v.id === c || v.variantId === c || v.value === c));
            variant['status'] = variant.id ? variant.status : 'ACTIVE'
            variant['name'] = product.articleName?.concat(" / ").concat(getVarinatname(variant.variants))
            prodVariants.push(variant);
        })
        setProduct(prev => ({
            ...prev,
            newProductVariants: prodVariants
        }));
        generateProductVariantMap();
    };

    const fetchVariant = async () => {
        try {
            const response = await B2B_API.get('variant').json();
            const groupedVariants = _.groupBy(response.response, 'name');
            setAttributes(groupedVariants);
            setVariants(response.response);
            setSelectedPairsFromProduct(response.response);
        } catch (error) {
            console.error('Error fetching variants:', error);
        }
    };

    const setSelectedPairsFromProduct = (masterVariantsList = variants) => {
        const prodVariants = {};

        const existingVariants = Array.isArray(product?.productVariants) && product.productVariants.length > 0
            ? product.productVariants
            : Array.isArray(product?.newProductVariants) && product.newProductVariants.length > 0
                ? product.newProductVariants
                : [];

        if (existingVariants.length > 0) {
            existingVariants.forEach(pv => {
                if (Array.isArray(pv.variants) && pv.variants.length > 0) {
                    pv.variants.forEach(v => {
                        if (v.name) {
                            if (!prodVariants[v.name]) {
                                prodVariants[v.name] = [];
                            }
                            const mv = masterVariantsList?.find(m => m.id === v.id || m.variantId === v.variantId || (v.variantId && m.id === v.variantId) || (v.id && m.variantId === v.id) || (m.name === v.name && m.value === v.value));
                            const targetId = mv ? mv.id : (v.id || v.variantId);
                            if (targetId && !prodVariants[v.name].includes(targetId)) {
                                prodVariants[v.name].push(targetId);
                            }
                        }
                    });
                } else if (pv.name && masterVariantsList && masterVariantsList.length > 0) {
                    let variantString = pv.name;
                    if (product.articleName && variantString.startsWith(product.articleName + " / ")) {
                        variantString = variantString.substring((product.articleName + " / ").length);
                    } else {
                        const parts = variantString.split(' / ');
                        if (parts.length > 1) {
                            variantString = parts.slice(1).join(' / ');
                        }
                    }
                    const nameParts = variantString.split(' / ').map(s => s.trim()).filter(Boolean);
                    masterVariantsList.forEach(mv => {
                        if (mv.name && mv.value && mv.id) {
                            if (nameParts.includes(mv.value) || variantString === mv.value) {
                                if (!prodVariants[mv.name]) {
                                    prodVariants[mv.name] = [];
                                }
                                if (!prodVariants[mv.name].includes(mv.id)) {
                                    prodVariants[mv.name].push(mv.id);
                                }
                            }
                        }
                    });
                }
            });
        }

        const keys = Object.keys(prodVariants);
        if (_.size(keys) > 0) {
            const pairs = keys.map(key => ({
                key,
                values: Array.from(new Set(prodVariants[key]))
            }));
            setSelectedPairs(pairs);
            setProduct(prev => ({
                ...prev,
                prodVariants: prodVariants,
            }));
        }
    };

    useEffect(() => {
        if (variants && variants.length > 0 && (product?.productVariants?.length > 0 || product?.newProductVariants?.length > 0)) {
            setSelectedPairsFromProduct(variants);
        }
    }, [product?.id, product?.productVariants, variants]);

    const handleSelectChange = (index, selectedValue) => {
        const newPairs = [...selectedPairs];
        const oldKey = newPairs[index].key;
        newPairs[index].key = selectedValue || '';
        if (!selectedValue) {
            newPairs[index].values = [];
        }
        const newKey = newPairs[index].key;
        if (newKey !== oldKey) {
            newPairs[index].key = selectedValue;
            newPairs[index].values = [];
        }
        setSelectedPairs(newPairs);
        const updatedProdVariants = { ...product.prodVariants };
        if (oldKey && oldKey !== selectedValue) {
            delete updatedProdVariants[oldKey];
        }
        if (selectedValue) {
            updatedProdVariants[selectedValue] = newPairs[index].values || [];
        }
        setProduct(prevState => ({ ...prevState, prodVariants: updatedProdVariants }));
        updateCombinations(newPairs);
    };

    const handleMultiSelectChange = (index, selectedValues) => {
        const newPairs = [...selectedPairs];
        newPairs[index].values = selectedValues;
        const updatedProdVariants = { ...product.prodVariants };
        updatedProdVariants[newPairs[index].key] = selectedValues;
        setSelectedPairs(newPairs);
        setProduct(prev => ({ ...prev, prodVariants: updatedProdVariants }));
        updateCombinations(newPairs);
    };

    const handleSelectSolidChange = (index, selectedValue) => {
        const newPairs = [...selectedPairs];
        newPairs[index].values = Array.isArray(selectedValue) ? selectedValue : [selectedValue];
        const updatedProdVariants = { ...product.prodVariants };
        updatedProdVariants[newPairs[index].key] = newPairs[index].values;
        setSelectedPairs(newPairs);
        setProduct(prev => ({ ...prev, prodVariants: updatedProdVariants }));
        updateCombinations(newPairs);
    };

    const addNewPair = () => {
        const newPairs = [...selectedPairs, { key: '', values: [] }];
        setSelectedPairs(newPairs);
        const newCombinations = [{ ...selectedPairs[0], values: [] }, ...combinations];
        setCombinations(newCombinations);
        updateCombinations(newPairs);
    };

    const removePair = (index) => {
        const newPairs = [...selectedPairs];
        const removedPairKey = newPairs[index].key;
        newPairs.splice(index, 1);
        setSelectedPairs(newPairs);
        const updatedProdVariants = { ...product.prodVariants };
        delete updatedProdVariants[removedPairKey];
        setProduct(prev => ({ ...prev, prodVariants: updatedProdVariants }));
        updateCombinations(newPairs);
    };

    const getAvailableKeys = (currentIndex) => {
        const selectedKeys = selectedPairs.map(pair => pair.key);
        return Object.keys(attributes).filter(key => !selectedKeys.includes(key) || selectedPairs[currentIndex].key === key);
    };


    const handleToggle = (index) => {
        if (expandedRows.includes(index)) {
            setExpandedRows(expandedRows.filter((i) => i !== index));
        } else {
            setExpandedRows([index]);
        }
    };

    const handleTabClick = (tabName) => {
        setActiveTab(tabName);
    };


    const handleTransaction = (index, key, event) => {
        setProduct((prevProduct) => {
            return {
                ...prevProduct,
                newProductVariants: prevProduct.newProductVariants.map((variant, i) => {
                    if (i === index) {

                        return {
                            ...variant,
                            [key]: 'true' === event?.target?.value, // Update the specific field's value
                        };
                    }

                    return { ...variant }; // Create a copy of the variant, ensuring no mutation
                }),
            };
        });
    };

    const json = [
        {
            label: "Stop GRN",
            type: 'radio',
            key: 'isStopGRN',
            fieldType: 'radioField',
            options: [
                { label: "Yes", value: true },
                { label: "No", value: false }
            ],
            onChange: (index, event) => handleTransaction(index, "isStopGRN", event),
            name: "isStopGRN",
        },
        {
            label: "Stop Purchase Return",
            type: 'radio',
            key: "isStopPurchaseReturn",
            fieldType: 'radioField',
            options: [
                { label: "Yes", value: true },
                { label: "No", value: false }
            ],
            onChange: (index, event) => handleTransaction(index, "isStopPurchaseReturn", event),
            name: "isStopPurchaseReturn",
        },
        {
            label: "Stop Sale",
            type: 'radio',
            key: "isStopSale",
            fieldType: 'radioField',
            options: [
                { label: "Yes", value: true },
                { label: "No", value: false }
            ],
            onChange: (index, event) => handleTransaction(index, "isStopSale", event),
            name: "isStopSale",
        },
        {
            label: "Allow Refund",
            type: 'radio',
            key: "isAllowRefund",
            fieldType: 'radioField',
            options: [
                { label: "Yes", value: true },
                { label: "No", value: false }
            ],
            onChange: (index, event) => handleTransaction(index, "isAllowRefund", event),
            name: "isAllowRefund",
        },
        {
            label: "Allow Negative",
            type: 'radio',
            key: "isAllowNegative",
            fieldType: 'radioField',
            options: [
                { label: "Yes", value: true },
                { label: "No", value: false }
            ],
            onChange: (index, event) => handleTransaction(index, "isAllowNegative", event),
            name: "isAllowNegative",
        },
        {
            label: "Allow Cost Edit",
            type: 'radio',
            key: "isAllowCostEditInGRN",
            fieldType: 'radioField',
            options: [
                { label: "Yes", value: true },
                { label: "No", value: false }
            ],
            onChange: (index, event) => handleTransaction(index, "isAllowCostEditInGRN", event),
            name: "isAllowCostEditInGRN",
        },
        {
            label: "Enable Serial Number",
            type: 'radio',
            key: "isEnableSerialNumber",
            fieldType: 'radioField',
            options: [
                { label: "Yes", value: true },
                { label: "No", value: false }
            ],
            onChange: (index, event) => handleTransaction(index, "isEnableSerialNumber", event),
            name: "isEnableSerialNumber",
        },
        {
            label: "Non-trading",
            type: 'radio',
            key: "isNonTrading",
            fieldType: 'radioField',
            options: [
                { label: "Yes", value: true },
                { label: "No", value: false }
            ],
            onChange: (index, event) => handleTransaction(index, "isNonTrading", event),
            name: "isNonTrading",
        },
    ];

    const handleEnable = (index) => {
        setProduct((prevProduct) => {
            return {
                ...prevProduct,
                newProductVariants: prevProduct.newProductVariants.map((variant, i) => {
                    if (i === index) {
                        return {
                            ...variant,
                            status: variant.status === "ACTIVE" ? "INACTIVE" : "ACTIVE",
                        };
                    }
                    return variant;
                }),
            };
        });
    };

    const getVarinatname = (variants) => {
        return variants.map(variant => variant?.value).join(' / ')
    }

    return (
        <section className="product-variant-section" style={{margin: "32px"}}>
            <div className="product-variant-section-wrap">
            </div>
            <div className="product-info-variant-mb1">
                <h3 className="product-info-variant-text-sub-heading product-info-variant-mb1" role="heading" aria-level="3">This product has {_.size(combinations)} variant</h3>
            </div>
            <div data-cy="variants-table-content">
                <table data-testid="table" className="product-info-variant-table-list">
                    <thead>
                        <tr data-testid="table-row" >
                            <th data-testid="table-head-cell" aria-sort="none">Variant name</th>
                            <th data-testid="table-head-cell" className="product-info-variant-table-list-head-cell" aria-sort="none">
                                <div>Variant SKU</div>
                            </th>
                        </tr>
                    </thead>
                    {
                        product?.newProductVariants?.map((item, index) => (
                            <tbody>
                                <tr key={index} data-testid="table-row" data-cy="variantSummary" className={`product-info-variant-table-list-row product-info-variant-table-list-row--expandable ${isRowExpanded(index) ? 'product-info-variant-table-list-row--expanded' : ''}`}>
                                    <td data-testid="table-body-cell"  >

                                        {item.name}

                                    </td>
                                    <td data-testid="table-body-cell" className="product-info-variant-table-list-cell product-info-variant-table-list-cell--input input-columns sku" colSpan="50">
                                        <div className="product-info-variant-util-pos-relative">
                                            <input className="product-info-variant-input variant-sku-input" type="text" data-cy="variant-sku-input" value={item?.variantSku} placeholder="Enter SKU" disabled style={{ cursor: 'not-allowed' }} />
                                        </div>
                                    </td>

                                </tr>
                                {isRowExpanded(index) && (
                                    <tr data-testid="table-row" className="product-info-variant-table-list-row product-info-variant-table-list-row--expanded-content">
                                        <td colSpan="50" data-testid="table-body-cell" className="product-info-variant-table-list-cell">
                                            <div className="product-info-variant-table-list-expanded-container">
                                                <div className="product-info-variant-table-list-expanded-content">
                                                    <div className="product-info-variant-tabs product-info-variant-mb5" role="tablist">
                                                        <div className="product-info-variant-tab" data-tab-name="Transaction">
                                                            <button
                                                                className={`product-info-variant-tab-button ${activeTab === 'Transaction' ? 'active-tab' : ''}`}
                                                                type="button"
                                                                role="tab"
                                                                data-cy="tab-button-Transaction"
                                                                data-testid="tab-button-Transaction"
                                                                onClick={() => handleTabClick('Transaction')}
                                                                disabled={vendorId}
                                                            >
                                                                Transaction
                                                            </button>
                                                        </div>
                                                    </div>
                                                    {activeTab === 'Image' && (
                                                        <div key={index}>
                                                            <div className="cn-variant-image-container">
                                                                <Dropzone
                                                                    onDrop={(acceptedFiles) => handleDrop(acceptedFiles, index)}
                                                                    openRef={openRef}
                                                                >
                                                                    {!item.image && !item.imageUrl ? (
                                                                        <>
                                                                            <IconUpload size={50} />
                                                                            <Text size="md">Drag images here or click to select files</Text>
                                                                        </>
                                                                    ) : <Text size="md">Click and choose file to change image</Text>}

                                                                    {item.imageUrl ? (
                                                                        <img src={item.imageUrl} alt="Uploaded" style={{ maxWidth: '100%', maxHeight: 100 }} />
                                                                    ) : (
                                                                        item.image && (
                                                                            <img src={`${BASE_URL}${item.image}?${Date.now()}`} alt="Uploaded" style={{ maxWidth: '100%', maxHeight: 100 }} />
                                                                        )
                                                                    )}

                                                                    {item.file && (
                                                                        <Text size="sm" mt={10}>
                                                                            Selected file: {item.file.name}
                                                                        </Text>
                                                                    )}
                                                                </Dropzone>
                                                            </div>
                                                            <div data-testid="inline-action-bar" className="product-info-variant-action-bar product-info-variant-action-bar--inline react-variant-image-action-bar product-info-variant-pl5 product-info-variant-pr5">
                                                                <Group spacing="sm">
                                                                    <Button onClick={() => handleReset(index)} color="red">Reset</Button>
                                                                </Group>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {activeTab === 'Transaction' && (
                                                        <div key={index} className='form-container'>
                                                            {json?.map((field, i) => (
                                                                <div key={i} className={"form-group"}>
                                                                    <label className='form-label'>{field.label}</label>

                                                                    <div className="radio-group">
                                                                        {field.options.map((option, idx) => (
                                                                            <div key={idx} className="radio-item" style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                                                                                <input
                                                                                    type="radio"
                                                                                    value={option.value}
                                                                                    name={field.name}
                                                                                    onChange={(e) => field.onChange(index, e)}
                                                                                    checked={item?.[field.key] === option.value}

                                                                                />
                                                                                <label className='radio-label'>{option.label}</label>
                                                                            </div>
                                                                        ))}
                                                                    </div>

                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        ))}
                </table>
            </div>
        </section>

    );
};

export default MappedProductVariant;